import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import numeral from 'numeral';

import ClientTimestamp from 'app/components/common/client-timestamp';
import FileInput       from 'app/components/common/file-input';
import Link            from 'app/components/common/link';
import Popper          from 'app/components/common/popper';
import RteViewer       from 'app/components/common/rich-text/viewer';
import NonprofitCard   from 'app/components/nonprofits/nonprofit-card';
import formsHelpers    from 'app/helpers/forms';
import currencies      from 'app/helpers/currencies';
import paths           from 'app/paths';
import AuthSlx         from 'app/selectors/auth';


const {FieldTypes} = formsHelpers;
const emptyArr = [];

class FormSubmission extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {};
  }

  get answers() {
    return this.props.submission?.formAnswers || [];
  }

  get fields() {
    return this.props.form?.formFields || [];
  }

  renderNoAnswer() {
    return <p className="answer-empty">no response given</p>
  }

  renderValueTextShort(value) {
    if (!(value || '').trim()) return this.renderNoAnswer();
    return <p>{value}</p>;
  }
  renderValueTextLong(value) {
    if (!(value || '').trim()) return this.renderNoAnswer();
    return <RteViewer md={value} />;
  }
  renderValueDate(value) {
    if (!value) return this.renderNoAnswer();
    const str = moment(value).format('MMMM Do, YYYY');
    return <p>{str}</p>;
  }
  renderValueNumber(value, answer) {
    if (!_.isFinite(value)) return this.renderNoAnswer();
    const decPlaces = answer.fieldConfig?.opts?.decPlaces || 0;
    const fmt = decPlaces ? `0,0.${new Array(decPlaces).fill(0).join('')}` : '0,0';
    return numeral(value).format(fmt);
  }
  renderValueMonetaryAmount(value) {
    if (!value) return this.renderNoAnswer();
    const {currencyCode, amount} = value;
    const cur = currencies.byCode[currencyCode];
    return <p>{cur.format(amount)}</p>;
  }
  renderValueNonprofit(value) {
    if (!value) return this.renderNoAnswer();
    return <NonprofitCard id={value} small target="_blank" />;
  }
  renderValueSelect(value) {
    if (!value) return this.renderNoAnswer();
    return <p>{value}</p>;
  }
  renderValueMultiSelect(value) {
    if (!value?.length) return this.renderNoAnswer();
    return (
      <ul>
        {value.map((val, i) => {
          return <li key={i}>{val}</li>;
        })}
      </ul>
    );
  }
  renderValueFile(value) {
    if (!value?.length) return this.renderNoAnswer();
    return <FileInput files={value} />;
  }
  renderValuePhone(value) {
    const number = value?.formatted;
    if (!number) return this.renderNoAnswer();
    const ans = `+${value.countryCode} ${number}`;
    return <p>{ans}</p>
  }
  renderValueAddress(value) {
    if (!value?.city && !value?.line1) return this.renderNoAnswer();
    let line3 = [value.city, value.stateCode].filter(p => p).join(', ');
    if (value.postalCode) line3 += ` ${value.postalCode}`;
    return (
      <p>
        {!!value.line1 && <><span>{value.line1}</span><br /></>}
        {!!value.line2 && <><span>{value.line2}</span><br /></>}
        {!!line3 && <><span>{line3}</span><br /></>}
      </p>
    );
  }

  renderValue(answer) {
    const {type} = answer.fieldConfig;
    const rawVal = answer.value[type];
    if (type === FieldTypes.TEXT_SHORT)       return this.renderValueTextShort(rawVal, answer);
    if (type === FieldTypes.TEXT_LONG)        return this.renderValueTextLong(rawVal, answer);
    if (type === FieldTypes.DATE)             return this.renderValueDate(rawVal, answer);
    if (type === FieldTypes.NUMBER)           return this.renderValueNumber(rawVal, answer);
    if (type === FieldTypes.MONETARY_AMOUNT)  return this.renderValueMonetaryAmount(rawVal, answer);
    if (type === FieldTypes.NONPROFIT)        return this.renderValueNonprofit(rawVal, answer);
    if (type === FieldTypes.SELECT)           return this.renderValueSelect(rawVal, answer);
    if (type === FieldTypes.MULTI_SELECT)     return this.renderValueMultiSelect(rawVal, answer);
    if (type === FieldTypes.FILE)             return this.renderValueFile(rawVal, answer);
    if (type === FieldTypes.PHONE)            return this.renderValuePhone(rawVal, answer);
    if (type === FieldTypes.ADDRESS)          return this.renderValueAddress(rawVal, answer);
    return null;
  }

  renderMeta() {
    const {submission, currentUser, nested} = this.props;
    if (nested) return null;
    const {user, employee} = submission;
    const isOwn = !!(user?.id && (user?.id === currentUser?.id));
    const person = employee || user;
    const name = person ? `${person.firstName} ${person.lastName}` : 'Anonymous';
    const email = person?.email || null;

    return (
      <div>
        <br /><br /><br /><br />
        <hr />
        <p className="italic">
          {`Submitted by ${name}`} {!!email && ` (${email})`} {` on `}
          <ClientTimestamp timestamp={submission.createdTs} format="MMM D, YYYY, h:mma" showTz />
        </p>
        {isOwn && (
          <Link href={paths.formSubmissionEdit(submission.id)}>edit my answers</Link>
        )}
      </div>
    );
  }

  render() {
    const {submission, form, nested, className, renderComments} = this.props;
    return (
      <div className={`fsub ${nested ? 'nested' : 'standalone'} ${className}`}>
        <h1 className="fsub-title">{form?.title || 'Form Submission'}</h1>
        <div className="fsub-anss">
          {this.answers.map((answer) => {
            return (
              <div key={answer.id} className="fsub-ans">
                <h2 className="fsub-ans-title">{answer.fieldConfig.title}</h2>
                <div className="fsub-ans-content">
                  {this.renderValue(answer)}
                </div>
                <br />
                {!!renderComments && renderComments(answer)}
              </div>
            );
          })}
        </div>
        {this.renderMeta()}
      </div>
    );
  }

}

FormSubmission.propTypes = {
  form: PropTypes.object,
  submission: PropTypes.object,
  nested: PropTypes.bool,
  className: PropTypes.string,
  renderComments: PropTypes.func,
};
FormSubmission.defaultProps = {
  nested: false,
  className: '',
  renderComments: null,
};

const stateToProps = (state) => ({
  currentUser: AuthSlx.currentUser(state),
});
const dispatchToProps = (dispatch) => ({});

export default connect(stateToProps, dispatchToProps)(FormSubmission);
